import { BACKEND_URL } from './backendUrl';
import * as authUtils from './auth';

const ZERO_DECIMAL_CURRENCIES = new Set([
  'bif', 'clp', 'djf', 'gnf', 'jpy', 'kmf', 'krw', 'mga', 'pyg',
  'rwf', 'ugx', 'vnd', 'vuv', 'xaf', 'xof', 'xpf'
]);

export const CREDIT_MODEL_COPY = '1 credit creates one fresh AI-generated planning result.';

export const isSubscriptionActive = (user) => {
  const status = user?.billing?.subscription_status;
  return ['active', 'trialing'].includes(String(status || '').toLowerCase());
};

export const getPlanningCredits = (user) => Number(user?.billing?.planning_credits || 0);

export const getIncludedPlanAllowance = (user) => Number(user?.billing?.paid_plan_allowance || 0);

export const isDevUnrestrictedAccount = (user) => {
  const email = String(user?.email || '').toLowerCase();
  return Boolean(user?.is_unrestricted)
    || email === 'sandheepr89@gmail.com'
    || email === 'sandheep.r.krishnan@gmail.com';
};

export const getDevBillingMode = () => {
  if (typeof window === 'undefined') return 'real';
  try {
    return localStorage.getItem('craftmyparty_dev_billing_mode') || 'real';
  } catch (_) {
    return 'real';
  }
};

export const getEffectiveBillingUser = (user) => {
  if (!user || !isDevUnrestrictedAccount(user)) return user;
  const devMode = getDevBillingMode();
  if (devMode === 'real') return user;

  const billing = { ...(user.billing || {}) };
  const usageStats = { ...(user.usage_stats || {}) };
  const nextUser = {
    ...user,
    is_unrestricted: false,
    billing,
    usage_stats: usageStats
  };

  if (devMode === 'new_free') {
    usageStats.theme_generations = 0;
    usageStats.plans_created = 0;
    billing.planning_credits = 0;
    billing.paid_plan_allowance = 0;
    billing.subscription_status = null;
  } else if (devMode === 'has_credit') {
    usageStats.theme_generations = 1;
    usageStats.plans_created = 1;
    billing.planning_credits = 1;
    billing.paid_plan_allowance = 1;
    billing.subscription_status = null;
  } else if (devMode === 'free_used_no_credits') {
    usageStats.theme_generations = 1;
    usageStats.plans_created = 1;
    billing.planning_credits = 0;
    billing.paid_plan_allowance = 0;
    billing.subscription_status = null;
  }

  return nextUser;
};

export const canGenerateFreshThemeSet = (user) => {
  if (!user) return false;
  if (user.is_unrestricted) return true;
  const themeRuns = Number(user?.usage_stats?.theme_generations || 0);
  return themeRuns < 1 || getPlanningCredits(user) > 0;
};

export const canGenerateMatchingPlan = (user) => {
  if (!user) return false;
  if (user.is_unrestricted) return true;
  const plansCreated = Number(user?.usage_stats?.plans_created || 0);
  return plansCreated < 1 || getIncludedPlanAllowance(user) > 0;
};

export const formatBillingMoney = (amountMinor, currency = 'usd', country = 'US', options = {}) => {
  const normalizedCurrency = String(currency || 'usd').toUpperCase();
  const divisor = ZERO_DECIMAL_CURRENCIES.has(normalizedCurrency.toLowerCase()) ? 1 : 100;
  const value = Number(amountMinor || 0) / divisor;
  const hasCents = Math.abs(value % 1) > 0.000001;

  return new Intl.NumberFormat(country === 'US' ? 'en-US' : undefined, {
    style: 'currency',
    currency: normalizedCurrency,
    minimumFractionDigits: options.forceCents || hasCents ? 2 : 0,
    maximumFractionDigits: options.forceCents || hasCents ? 2 : 0
  }).format(value);
};

export const formatDate = (value) => {
  if (!value) return null;
  const date = typeof value === 'number' ? new Date(value * 1000) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

export const splitBillingPackages = (packages = []) => ({
  creditPacks: packages.filter((pkg) => pkg.kind === 'credit_pack'),
  subscriptions: packages.filter((pkg) => pkg.kind === 'subscription')
});

export const getBillingOfferTitle = (pkg) => {
  if (!pkg) return '';
  if (pkg.id === 'credit_single') return '1 Credit Pack';
  if (pkg.id === 'credit_5') return '5 Credit Pack';
  if (pkg.id === 'credit_10') return '10 Credit Pack';
  if (pkg.id === 'plus_monthly') return 'Monthly planner';
  if (pkg.id === 'plus_annual') return 'Power planner';
  return pkg.name;
};

export const getOfferDescription = (pkg) => {
  if (!pkg) return '';
  if (pkg.id === 'credit_single') {
    return 'Best for planning a single event or trying premium generations without a subscription. One-time purchase, no monthly commitment. Unused credits stay in your account for future plans.';
  }
  if (pkg.id === 'credit_5') {
    return 'Best for hosts planning multiple events or trying several custom concepts. One-time purchase, no monthly commitment. Unused credits stay in your account for future plans.';
  }
  if (pkg.id === 'credit_10') {
    return 'Best value for frequent party planning, professional hosts, and repeat events. One-time purchase, no monthly commitment. Unused credits stay in your account for future plans.';
  }
  if (pkg.id === 'plus_monthly') {
    return 'Best for families, hosts, and creators planning multiple events. Includes 2 credits each month, lower effective cost. Unused monthly credits roll over so you can use them for future plans. Manage or cancel anytime through Stripe.';
  }
  if (pkg.id === 'plus_annual') {
    return 'Best for frequent hosts, party planners, and people creating multiple party concepts. Includes 24 credits per year, delivered as 2 credits each month (best value). Unused monthly credits roll over so you can use them for future plans. Manage or cancel anytime through Stripe.';
  }
  return pkg.description || '';
};

export const getOfferFeatures = (pkg) => {
  if (!pkg) return [];
  switch (pkg.id) {
    case 'credit_single':
      return [
        'Best for planning a single event',
        '1 fresh AI-generated planning result',
        'One-time purchase, no subscription',
        'Unused credits stay in your account'
      ];
    case 'credit_5':
      return [
        'Best for hosts planning multiple events',
        '5 fresh AI-generated planning results',
        'One-time purchase (Save 10%)',
        'Unused credits stay in your account'
      ];
    case 'credit_10':
      return [
        'Best for professional hosts & creators',
        '10 fresh AI-generated planning results',
        'One-time purchase (Save 20%)',
        'Unused credits stay in your account'
      ];
    case 'plus_monthly':
      return [
        'Best for families & active hosts',
        'Includes 2 credits each month',
        'Unused monthly credits roll over',
        'Manage or cancel anytime through Stripe'
      ];
    case 'plus_annual':
      return [
        'Best for power hosts & party planners',
        '24 credits/year (delivered as 2/month)',
        'Unused monthly credits roll over',
        'Manage or cancel anytime through Stripe'
      ];
    default:
      return pkg.description ? [pkg.description] : [];
  }
};

export const getBillingOfferPrice = (pkg, country = 'US') => {
  if (!pkg) return '';
  const base = formatBillingMoney(pkg.amount, pkg.currency, country);
  if (pkg.interval === 'month') return `${base}/mo`;
  if (pkg.interval === 'year') return `${base}/yr`;
  return base;
};

export const getPerCreditCopy = (pkg, country = 'US') => {
  if (!pkg?.per_credit_amount) return null;
  const perCredit = formatBillingMoney(pkg.per_credit_amount, pkg.currency, country, { forceCents: true });
  if (pkg.kind === 'subscription') {
    return `${perCredit} per included credit`;
  }
  return `${perCredit} per credit`;
};

export const getSavingsCopy = (pkg, country = 'US') => {
  if (!pkg?.savings_amount || !pkg?.savings_percent) return null;
  const amount = formatBillingMoney(pkg.savings_amount, pkg.currency, country, { forceCents: true });
  return `Save ${Number(pkg.savings_percent).toFixed(1).replace(/\.0$/, '')}% (${amount})`;
};

const BILLING_CACHE_TTL_MS = 10 * 60 * 1000;
const PACKAGES_REFRESH_MIN_INTERVAL_MS = 5 * 60 * 1000;

const getBillingCacheKey = (country = 'US') => `craftmyparty_billing_packages_${String(country || 'US').toUpperCase()}`;

const normalizeBillingCountry = (country = 'US') => String(country || 'US').toUpperCase();

let packagesFetchInFlight = null;
let packagesLastFetchedAt = 0;

export const getCachedBillingPackages = (country = 'US') => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(getBillingCacheKey(country));
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (!cached?.data || Date.now() - Number(cached.cachedAt || 0) > BILLING_CACHE_TTL_MS) {
      return null;
    }
    return cached.data;
  } catch (_) {
    return null;
  }
};

const cacheBillingPackages = (country = 'US', data) => {
  if (typeof window === 'undefined' || !data) return;
  try {
    sessionStorage.setItem(
      getBillingCacheKey(country),
      JSON.stringify({ cachedAt: Date.now(), data })
    );
  } catch (_) {}
};

export const clearBillingPackagesCache = (country = 'US') => {
  if (typeof window === 'undefined') return;
  packagesFetchInFlight = null;
  packagesLastFetchedAt = 0;
  try {
    sessionStorage.removeItem(getBillingCacheKey(country));
  } catch (_) {}
};

const fetchBillingPackagesFromNetwork = async (country = 'US', refresh = false) => {
  const billingCountry = normalizeBillingCountry(country);
  const params = new URLSearchParams({ country: billingCountry });
  if (refresh) params.set('refresh', 'true');
  const response = await fetch(`${BACKEND_URL}/api/billing/packages?${params.toString()}`);
  if (response.status === 429) {
    const cached = getCachedBillingPackages(billingCountry);
    if (cached) return cached;
    throw new Error('Billing packages are temporarily rate limited. Please try again shortly.');
  }
  if (!response.ok) throw new Error('Failed to load billing packages');
  const data = await response.json();
  cacheBillingPackages(billingCountry, data);
  packagesLastFetchedAt = Date.now();
  return data;
};

/**
 * Load billing packages once per cache window. Dedupes concurrent callers and
 * never spams refresh=true (Stripe price refresh) on every screen mount.
 */
export const fetchBillingPackages = async (country = 'US', refresh = false) => {
  const billingCountry = normalizeBillingCountry(country);

  if (!refresh) {
    const cached = getCachedBillingPackages(billingCountry);
    if (cached && cached.packages && cached.packages.length > 0) return cached;
  } else if (Date.now() - packagesLastFetchedAt < PACKAGES_REFRESH_MIN_INTERVAL_MS) {
    const cached = getCachedBillingPackages(billingCountry);
    if (cached && cached.packages && cached.packages.length > 0) return cached;
  }

  if (packagesFetchInFlight) {
    return packagesFetchInFlight;
  }

  packagesFetchInFlight = fetchBillingPackagesFromNetwork(billingCountry, refresh)
    .finally(() => {
      packagesFetchInFlight = null;
    });

  return packagesFetchInFlight;
};

if (typeof window !== 'undefined' && getCachedBillingPackages('US')) {
  packagesLastFetchedAt = Date.now();
}

export const createCheckoutSession = async ({ packageId, country = 'US', successUrl, cancelUrl }) => {
  const buildRequest = async () => fetch(`${BACKEND_URL}/api/billing/create-checkout-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await authUtils.getFreshAuthHeader())
    },
    body: JSON.stringify({
      package_id: packageId,
      country,
      success_url: successUrl,
      cancel_url: cancelUrl
    })
  });
  let response = await buildRequest();
  if (response.status === 401 && authUtils.getRefreshToken()) {
    await authUtils.refreshAccessToken();
    response = await buildRequest();
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || 'Could not start checkout');
  return data;
};

export const createPortalSession = async (returnUrl) => {
  const buildRequest = async () => fetch(`${BACKEND_URL}/api/billing/create-portal-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await authUtils.getFreshAuthHeader())
    },
    body: JSON.stringify({ return_url: returnUrl })
  });
  let response = await buildRequest();
  if (response.status === 401 && authUtils.getRefreshToken()) {
    await authUtils.refreshAccessToken();
    response = await buildRequest();
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || 'Could not open subscription management');
  return data;
};
